import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface WaitlistPayload {
  email: string;
  full_name?: string;
  company_name?: string;
  role_title?: string;
  team_size?: string;
  interest_area?: string;
  notes?: string;
  source?: string;
}

export type WaitlistState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success" }
  | { status: "duplicate" }
  | { status: "error"; message: string };

export function useWaitlist() {
  const [state, setState] = useState<WaitlistState>({ status: "idle" });

  async function submit(payload: WaitlistPayload) {
    setState({ status: "submitting" });

    const row = {
      email: payload.email.trim().toLowerCase(),
      full_name: payload.full_name?.trim() || null,
      company_name: payload.company_name?.trim() || null,
      role_title: payload.role_title?.trim() || null,
      team_size: payload.team_size?.trim() || null,
      interest_area: payload.interest_area?.trim() || null,
      notes: payload.notes?.trim() || null,
      source: payload.source ?? "growth_suite_waitlist",
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
    };

    const { error } = await supabase.from("early_access_requests").insert(row);

    if (error) {
      // Unique violation (already on waitlist for this source)
      if ((error as { code?: string }).code === "23505") {
        setState({ status: "duplicate" });
        return { ok: true, duplicate: true } as const;
      }
      const message = error.message || "Could not save your request. Please try again.";
      setState({ status: "error", message });
      return { ok: false, error: message } as const;
    }

    setState({ status: "success" });
    return { ok: true, duplicate: false } as const;
  }

  function reset() {
    setState({ status: "idle" });
  }

  return { state, submit, reset } as const;
}